/**
 * Formatting for the Support metrics page and the CSAT shown on a closed
 * session. Pure, so it is unit-tested on its own.
 */

import { ChatMetrics } from '../../hooks/chat/api';

/** The ranges the page offers, in days (ant allows at most 92). */
export const RANGE_DAYS = [7, 30, 90] as const;
export type RangeDays = typeof RANGE_DAYS[number];

const DAY_MS = 86400000;

/** Midnight UTC of the day `ms` falls in. */
const utcDayStart = (ms: number): number => Math.floor(ms / DAY_MS) * DAY_MS;

/**
 * [from, to) for the last `days` UTC days, today included: from midnight
 * UTC `days - 1` days ago up to now, so the chart has exactly `days` bars.
 */
export function metricsRange(
  days: number,
  now: Date = new Date()
): { from: string; to: string } {
  const to = now.getTime();
  const from = utcDayStart(to) - (days - 1) * DAY_MS;
  return { from: new Date(from).toISOString(), to: new Date(to).toISOString() };
}

/** "—" for no value; else "45s", "3m 20s", "1h 5m", "2d 3h". */
export function formatDuration(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined || Number.isNaN(seconds)) return '—';
  const s = Math.max(0, Math.round(seconds));
  if (s < 60) return `${s}s`;
  const minutes = Math.floor(s / 60);
  if (minutes < 60) {
    const rest = s % 60;
    return rest ? `${minutes}m ${rest}s` : `${minutes}m`;
  }
  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    const rest = minutes % 60;
    return rest ? `${hours}h ${rest}m` : `${hours}h`;
  }
  const days = Math.floor(hours / 24);
  const rest = hours % 24;
  return rest ? `${days}d ${rest}h` : `${days}d`;
}

/** The CSAT card: "4.25" out of 5 and "12 ratings", or "—" with none. */
export function formatCsat(
  average: number | null | undefined,
  count: number
): { value: string; detail: string } {
  if (!count || average === null || average === undefined) {
    return { value: '—', detail: 'No ratings yet' };
  }
  return {
    value: average.toFixed(2),
    detail: `${count} rating${count === 1 ? '' : 's'}`,
  };
}

/** Closed as a share of sessions: "80%", or "—" without sessions. */
export function closedShare(sessions: number, closed: number): string {
  if (!sessions) return '—';
  return `${Math.round((closed / sessions) * 100)}%`;
}

/** Every UTC day of [from, to) with its sessions, the days ant left out as 0. */
export function fillDays(
  byDay: ChatMetrics['byDay'],
  from: string,
  to: string
): Array<{ day: string; sessions: number }> {
  const counts = new Map(byDay.map((d) => [d.day, d.sessions]));
  const start = utcDayStart(Date.parse(from));
  const endMs = Date.parse(to);
  if (Number.isNaN(start) || Number.isNaN(endMs)) return [...byDay];
  const out: Array<{ day: string; sessions: number }> = [];
  for (let day = start; day < endMs; day += DAY_MS) {
    const key = new Date(day).toISOString().slice(0, 10);
    out.push({ day: key, sessions: counts.get(key) ?? 0 });
  }
  return out;
}

/** "Sep 25" for a UTC day key, as the chart's axis labels it. */
export function dayLabel(day: string): string {
  const d = new Date(`${day}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return day;
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

const HANDLERS: Record<string, string> = {
  human: 'People',
  assistant: 'Assistant',
};

/** Who handled the sessions, as the table names them. */
export const handlerLabel = (handledBy: string): string =>
  HANDLERS[handledBy.toLowerCase()] ??
  handledBy.charAt(0).toUpperCase() + handledBy.slice(1).replace(/_/g, ' ');

/** A share of the sessions, for the tables: "25%". */
export const shareOf = (count: number, total: number): string =>
  total ? `${Math.round((count / total) * 100)}%` : '—';

/** How a rating reads: happy (4-5), neutral (3) or unhappy (1-2). */
export function csatTone(score: number): 'success' | 'warning' | 'error' {
  if (score >= 4) return 'success';
  if (score === 3) return 'warning';
  return 'error';
}
